import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";

import { ProductData } from "../../apiHooks";
import { Typography } from "../../components";
import { Button } from "../../components/Button";
import { ProductAvailabilityPricingCategory } from "../../product/ProductAvailabilityPricingCategory";
import { AvailabilityDateRangePicker } from "./AvailabilityDateRangePicker";

type AvailabilityHotelProps = {
    product: ProductData;
    onChange: (availabilityListItem: string | null, isDefault: boolean) => void;
};

type UpdateProps = Partial<HotelRoom>;

export type HotelRoom = {
    adultCount: number;
    childList: {
        age: number;
    }[];
};

type HotelRoomListState = {
    roomList: HotelRoom[];
};

const RoomInput = ({
    roomIndex,
    updateRoom,
    room,
}: {
    roomIndex: number;
    updateRoom: (newState: UpdateProps) => void;
    room: HotelRoom;
}) => {
    const { adultCount, childList } = room;
    return (
        <StyledDiv>
            <Typography size="small" variant="title">
                {`Room ${roomIndex + 1}`}
            </Typography>

            <ProductAvailabilityPricingCategory
                label="Adults"
                showPrice={false}
                description={null}
                price={{
                    amount: 0,
                    currency: "USD",
                }}
                units={adultCount}
                maxParticipantsDepends={[]}
                min={1}
                max={null}
                minAge={null}
                maxAge={null}
                discountsAvailable={false}
                onUnitsChange={(adultCount) => {
                    updateRoom({ adultCount });
                }}
            />
            <ProductAvailabilityPricingCategory
                label="Children"
                showPrice={false}
                description={null}
                price={{
                    amount: 0,
                    currency: "USD",
                }}
                units={childList.length}
                min={1}
                max={null}
                maxParticipantsDepends={[]}
                minAge={0}
                maxAge={17}
                discountsAvailable={false}
                onUnitsChange={(x) => {
                    const newChildList = [...childList];
                    const isIncreaed = x > childList.length;

                    if (isIncreaed) {
                        newChildList.push({ age: 0 });
                    } else {
                        newChildList.pop();
                    }

                    updateRoom({ childList: newChildList });
                }}
            />
            {childList.map((value, index) => (
                <FormControl fullWidth variant="outlined" key={index}>
                    <InputLabel>{`Child ${index + 1} age`}</InputLabel>
                    <SelectStyled
                        label={`Child ${index + 1} age`}
                        value={value.age}
                        onChange={(event) => {
                            const selectedAge = event.target.value;
                            const updatedChildList = [...childList];
                            updatedChildList[index] = { age: Number(selectedAge) };
                            updateRoom({ childList: updatedChildList });
                        }}
                    >
                        {Array.from({ length: 18 }, (_, age) => (
                            <MenuItem key={age} value={age}>
                                {age === 0 ? "under 1" : age}
                            </MenuItem>
                        ))}
                    </SelectStyled>
                </FormControl>
            ))}
        </StyledDiv>
    );
};
export const AvailabilityHotelModalHeader = ({ product, onChange }: AvailabilityHotelProps) => {
    const [state, setState] = useState<HotelRoomListState>({
        roomList: [{ adultCount: 1, childList: [] }],
    });

    const updateRoom = (index: number, newState: UpdateProps) => {
        const updatedRoomList = [...state.roomList];
        updatedRoomList[index] = {
            ...updatedRoomList[index],
            ...newState,
        };

        setState({
            ...state,
            roomList: updatedRoomList,
        });
    };

    const onAddRoom = () => {
        const updatedRoomList = [...state.roomList];
        updatedRoomList.push({ adultCount: 1, childList: [] });

        setState({ ...state, roomList: updatedRoomList });
    };

    const onRemoveRoom = (index: number) => {
        const updatedRoomList = [...state.roomList];
        updatedRoomList.splice(index, 1);

        setState({ ...state, roomList: updatedRoomList });
    };

    const showAddButton = state.roomList.length > 1 && state.roomList.length < 8;
    return (
        <FormControl fullWidth>
            <AvailabilityDateRangePicker productId={product.id} roomList={state.roomList} onChange={onChange} />
            {state.roomList.map((value, index) => (
                <StyledDiv key={index} style={{ paddingTop: 15 }}>
                    <RoomInput
                        roomIndex={index}
                        room={value}
                        updateRoom={(newState: UpdateProps) => updateRoom(index, newState)}
                    />
                    {state.roomList.length > 1 ? (
                        <StyledButton color="secondary" onClick={() => onRemoveRoom(index)} startIcon={<CloseIcon />}>
                            {"Remove room"}
                        </StyledButton>
                    ) : (
                        <StyledButton color="primary" onClick={onAddRoom} startIcon={<AddIcon />}>
                            {"Add another room"}
                        </StyledButton>
                    )}
                </StyledDiv>
            ))}

            {showAddButton && (
                <StyledButton color="primary" onClick={onAddRoom} startIcon={<AddIcon />}>
                    {"Add another room"}
                </StyledButton>
            )}
        </FormControl>
    );
};

const StyledButton = styled(Button)(() => ({
    justifyContent: "end",
}));

const StyledDiv = styled("div")({
    display: "flex",
    gap: 12,
    flexDirection: "column",
});

const SelectStyled = styled(Select)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
}));
